import { useState, useMemo } from 'react';
import ProductCard from './ProductCard';
import { useProducts } from '../../../hooks/useProducts';

interface ProductGridProps {
  selectedCategory: string;
  selectedSubcategory: string;
  priceRange: number[];
  selectedColors: string[];
  selectedSizes: string[];
  sortBy: string;
  onAddToCart: (product: any, color: string, size: string, quantity: number) => void;
}

export default function ProductGrid({
  selectedCategory,
  selectedSubcategory,
  priceRange,
  selectedColors,
  selectedSizes,
  sortBy,
  onAddToCart
}: ProductGridProps) {
  const [viewMode, setViewMode] = useState('grid');
  const { products, loading, refetch } = useProducts();
  const [isRefreshing, setIsRefreshing] = useState(false);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await refetch();
    setIsRefreshing(false);
  };

  const filteredProducts = useMemo(() => {
    let filtered = products.filter(product => {
      // Category filter
      if (selectedCategory !== 'all' && product.category !== selectedCategory) {
        return false;
      }

      // Subcategory filter
      if (selectedSubcategory !== 'all' && product.subcategory !== selectedSubcategory) {
        return false;
      }

      // Price filter
      const productPrice = parseFloat(product.price) || 0;
      if (productPrice < priceRange[0] || productPrice > priceRange[1]) {
        return false;
      }

      // Color filter
      if (selectedColors.length > 0 && product.colors) {
        const productColors = Array.isArray(product.colors) ? product.colors : [];
        const hasMatchingColor = productColors.some(color => 
          selectedColors.includes(color)
        );
        if (!hasMatchingColor) return false;
      }

      // Size filter
      if (selectedSizes.length > 0 && product.sizes) {
        const productSizes = Array.isArray(product.sizes) ? product.sizes : [];
        const hasMatchingSize = productSizes.some(size => 
          selectedSizes.includes(size)
        );
        if (!hasMatchingSize) return false;
      }

      return true;
    });

    // Sort products
    switch (sortBy) {
      case 'price-low':
        filtered.sort((a, b) => (parseFloat(a.price) || 0) - (parseFloat(b.price) || 0));
        break;
      case 'price-high':
        filtered.sort((a, b) => (parseFloat(b.price) || 0) - (parseFloat(a.price) || 0));
        break;
      case 'name':
        filtered.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        break;
      case 'rating':
        filtered.sort((a, b) => (b.rating || 0) - (a.rating || 0));
        break;
      case 'newest':
        filtered.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
        break;
      default:
        // Featured - keep original order
        break;
    }

    return filtered;
  }, [products, selectedCategory, selectedSubcategory, priceRange, selectedColors, selectedSizes, sortBy]);

  if (loading) {
    return (
      <div className="text-center py-16">
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-600"></div>
        <p className="mt-4 text-gray-600">Loading products...</p>
      </div>
    );
  }

  return (
    <div>
      {/* Results Header */}
      <div className="flex items-center justify-between mb-6 bg-white p-4 rounded-lg shadow-sm">
        <div>
          <p className="text-gray-600 font-medium">
            Showing {filteredProducts.length} of {products.length} products
          </p>
        </div>
        
        <div className="flex items-center space-x-3">
          {/* Refresh Button - More Visible */}
          <button
            onClick={handleRefresh}
            disabled={isRefreshing}
            className="flex items-center space-x-2 px-5 py-2.5 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 transition-all cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap shadow-md hover:shadow-lg font-medium"
            title="Sync with Shopify"
          >
            <i className={`ri-refresh-line text-lg ${isRefreshing ? 'animate-spin' : ''}`}></i>
            <span>{isRefreshing ? 'Syncing...' : 'Sync'}</span>
          </button>

          {/* View Mode Buttons */}
          <div className="flex items-center space-x-2 border-l pl-3 ml-3">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 rounded transition-colors cursor-pointer ${viewMode === 'grid' ? 'bg-cyan-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
              title="Grid View"
            >
              <i className="ri-grid-line text-lg"></i>
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-2 rounded transition-colors cursor-pointer ${viewMode === 'list' ? 'bg-cyan-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
              title="List View"
            >
              <i className="ri-list-check text-lg"></i>
            </button>
          </div>
        </div>
      </div>

      {/* Products Grid */}
      <div className={`${
        viewMode === 'grid' 
          ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6' 
          : 'space-y-6'
      }`} data-product-shop>
        {filteredProducts.map((product) => (
          <ProductCard
            key={product.id}
            product={product}
            viewMode={viewMode}
            onAddToCart={onAddToCart}
          />
        ))}
      </div>

      {filteredProducts.length === 0 && !loading && (
        <div className="text-center py-16">
          <i className="ri-search-line text-6xl text-gray-300 mb-4 w-16 h-16 flex items-center justify-center mx-auto"></i>
          <h3 className="text-xl font-semibold text-gray-600 mb-2">No products found</h3>
          <p className="text-gray-500">
            {products.length === 0 
              ? 'No products available yet. Add products from your Shopify store.' 
              : 'Try adjusting your filters to see more results'}
          </p>
        </div>
      )}
    </div>
  );
}
